import { useState, useRef, useEffect } from 'react';
import { PROSPECTS, USERS } from './data';

// ── SYSTEM PROMPT ─────────────────────────────────────────────────────────────
const buildSystemPrompt = (user) => `
You are the OrchestrateX Quantum CRM Agent — an intelligent assistant embedded in the OrchestrateX internal BD pipeline platform. You serve the five co-founding team members across Business Development, Partnerships, and Product.

Current user: ${user.name} (${user.role})

## YOUR CAPABILITIES
You can answer questions about and take actions on the CRM data. Always respond with structured JSON in this exact format:

{
  "message": "<your human-readable response to the user>",
  "action": "<one of: none | filter | highlight | update_stage | add_note | draft_email | summarize | export>",
  "payload": <action-specific data or null>
}

## ACTIONS REFERENCE

**filter** — Return a filtered view of prospects
payload: { "ids": [array of prospect IDs matching the query] }

**highlight** — Highlight a specific prospect's detail panel
payload: { "id": <prospect id> }

**update_stage** — Suggest a stage change (user must confirm)
payload: { "id": <prospect id>, "newStage": "<stage name>", "reason": "<why>" }

**add_note** — Append intelligence to a prospect record
payload: { "id": <prospect id>, "note": "<text to append>" }

**draft_email** — Generate an outreach email
payload: { "to": "<name / company>", "subject": "<subject>", "body": "<full email body>" }

**summarize** — Return a structured summary
payload: { "title": "<summary title>", "content": "<formatted text>" }

**export** — Prepare a data export summary
payload: { "data": "<CSV-formatted string of matching records>" }

## CRM DATA SNAPSHOT
Total prospects: ${PROSPECTS.length}
Federal accounts: ${PROSPECTS.filter(p=>p.type==='federal').length}
Enterprise accounts: ${PROSPECTS.filter(p=>p.type==='customer').length}
Channel partners: ${PROSPECTS.filter(p=>p.type==='partner').length}
Investors/Board: ${PROSPECTS.filter(p=>p.type==='investor').length}
Critical priority: ${PROSPECTS.filter(p=>p.priority==='Critical').length}
Active/Negotiation: ${PROSPECTS.filter(p=>p.stage==='Active'||p.stage==='Negotiation'||p.stage?.includes('Business Case')).length}

## FULL PROSPECT LIST (for reference)
${PROSPECTS.map(p=>`ID:${p.id} | ${p.company} | ${p.type} | ${p.priority} | ${p.stage} | ARR:${p.arr} | Contact:${p.contact} | Tags:${(p.tags||[]).join(',')} | OX Drivers:${(p.oxDrivers||'').slice(0,120)}`).join('\n')}

## RULES
- Always respond with valid JSON only. No markdown, no preamble.
- Be direct, precise, and executive-level in tone — this team operates at co-founder speed.
- When drafting emails, use Chris Voss negotiation framing: anchor on value, use calibrated questions, create urgency through deadlines not pressure.
- For federal prospects, always emphasize Andy's SSA suitability clearance and the 77-sprint TSRP track record.
- Reference specific ARR ranges, margin, and integration status when discussing commercial terms.
- If asked about a specific company, pull all relevant data from the prospect list above.
- If asked to update data, always confirm the action before executing.
`.trim();

// ── EXAMPLE PROMPTS ───────────────────────────────────────────────────────────
const EXAMPLE_PROMPTS = [
  { label: 'Show all Critical prospects', prompt: 'Show me all Critical priority prospects' },
  { label: 'Federal pipeline summary', prompt: 'Summarize our full federal government pipeline with ARR totals and stages' },
  { label: 'Draft Avaya outreach', prompt: 'Draft an executive outreach email to Jerry Dotson at Avaya about closing the MRA this week' },
  { label: 'Partners in Negotiation', prompt: 'Which channel partners are currently in Negotiation or active deal stage?' },
  { label: 'Highest ARR opportunities', prompt: 'Rank our top 5 opportunities by ARR potential' },
  { label: 'SSA status update', prompt: 'Give me a full status update on the SSA account including integration, stage, and next steps' },
  { label: 'Move IRS to Active', prompt: 'Update IRS stage to Active — Bisignano confirmed cross-agency mandate on the call today' },
  { label: 'Healthcare vertical', prompt: 'What is our healthcare vertical coverage and what product asks are common across those accounts?' },
  { label: 'Export federal accounts', prompt: 'Export all federal accounts with company, contact, ARR, stage, and integration status' },
  { label: 'Gap analysis', prompt: 'What channel partner gaps exist in our CCaaS coverage that we should prioritize?' },
];

// ── MESSAGE BUBBLE ────────────────────────────────────────────────────────────
function MessageBubble({ msg, onAction }) {
  const isUser = msg.role === 'user';
  const isSystem = msg.role === 'system';

  if (isSystem) {
    return (
      <div style={{ textAlign: 'center', padding: '8px 0' }}>
        <span style={{ fontSize: 10, color: 'var(--ox-dim)', background: 'var(--ox-navy3)', padding: '3px 10px', borderRadius: 10 }}>
          {msg.content}
        </span>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: isUser ? 'row-reverse' : 'row', gap: 10, marginBottom: 14, alignItems: 'flex-start' }}>
      {!isUser && (
        <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--ox-navy3)', border: '1px solid var(--ox-border2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, flexShrink: 0 }}>
          ⬡
        </div>
      )}
      <div style={{ maxWidth: '82%' }}>
        <div style={{
          background: isUser ? 'var(--ox-blue)' : 'var(--ox-navy3)',
          border: `1px solid ${isUser ? 'var(--ox-blue)' : 'var(--ox-border)'}`,
          borderRadius: isUser ? '12px 12px 2px 12px' : '2px 12px 12px 12px',
          padding: '10px 14px',
          fontSize: 12.5,
          color: 'var(--ox-text)',
          lineHeight: 1.6,
          whiteSpace: 'pre-wrap',
        }}>
          {msg.content}
        </div>

        {/* Action blocks */}
        {msg.action && msg.action !== 'none' && msg.payload && (
          <ActionBlock action={msg.action} payload={msg.payload} onAction={onAction} />
        )}

        <div style={{ fontSize: 10, color: 'var(--ox-dim)', marginTop: 4, textAlign: isUser ? 'right' : 'left' }}>
          {msg.time}
        </div>
      </div>
    </div>
  );
}

// ── ACTION BLOCK ──────────────────────────────────────────────────────────────
function ActionBlock({ action, payload, onAction }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const base = {
    marginTop: 8,
    background: 'var(--ox-navy2)',
    border: '1px solid var(--ox-border2)',
    borderRadius: 8,
    padding: '10px 14px',
    fontSize: 12,
  };

  if (action === 'filter' && payload?.ids) {
    return (
      <div style={base}>
        <div style={{ fontSize: 10, color: 'var(--ox-dim)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 1 }}>
          Filter result — {payload.ids.length} prospect{payload.ids.length !== 1 ? 's' : ''}
        </div>
        <button
          onClick={() => onAction('filter', payload.ids)}
          style={{ background: 'var(--ox-blue)', border: 'none', borderRadius: 5, padding: '6px 14px', color: '#fff', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit' }}
        >
          Apply filter to table →
        </button>
      </div>
    );
  }

  if (action === 'highlight' && payload?.id) {
    return (
      <div style={base}>
        <button
          onClick={() => onAction('highlight', payload.id)}
          style={{ background: 'var(--ox-navy3)', border: '1px solid var(--ox-border2)', borderRadius: 5, padding: '6px 14px', color: 'var(--ox-blue2)', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit' }}
        >
          Open record →
        </button>
      </div>
    );
  }

  if (action === 'update_stage' && payload) {
    return (
      <div style={base}>
        <div style={{ fontSize: 10, color: 'var(--ox-dim)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 1 }}>Proposed stage change</div>
        <div style={{ fontSize: 12, color: 'var(--ox-amber)', marginBottom: 4 }}>→ {payload.newStage}</div>
        <div style={{ fontSize: 11, color: 'var(--ox-muted)', marginBottom: 8 }}>{payload.reason}</div>
        <button
          onClick={() => onAction('update_stage', payload)}
          style={{ background: 'var(--ox-teal)', border: 'none', borderRadius: 5, padding: '6px 14px', color: '#fff', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit' }}
        >
          Confirm update →
        </button>
      </div>
    );
  }

  if (action === 'draft_email' && payload) {
    const full = `To: ${payload.to}\nSubject: ${payload.subject}\n\n${payload.body}`;
    return (
      <div style={base}>
        <div style={{ fontSize: 10, color: 'var(--ox-dim)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 1 }}>Draft outreach</div>
        <div style={{ fontSize: 11, color: 'var(--ox-blue2)', marginBottom: 2 }}>To: {payload.to}</div>
        <div style={{ fontSize: 11, color: 'var(--ox-text)', fontWeight: 500, marginBottom: 8 }}>Subject: {payload.subject}</div>
        <div style={{ fontSize: 11, color: 'var(--ox-text)', lineHeight: 1.7, whiteSpace: 'pre-wrap', background: 'var(--ox-navy)', padding: '8px 10px', borderRadius: 6, border: '1px solid var(--ox-border)', maxHeight: 200, overflowY: 'auto', marginBottom: 10 }}>
          {payload.body}
        </div>
        <button
          onClick={() => handleCopy(full)}
          style={{ background: copied ? 'var(--ox-teal)' : 'var(--ox-navy)', border: '1px solid var(--ox-border2)', borderRadius: 5, padding: '6px 14px', color: copied ? '#fff' : 'var(--ox-text)', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit' }}
        >
          {copied ? '✓ Copied' : 'Copy to clipboard'}
        </button>
      </div>
    );
  }

  if (action === 'summarize' && payload) {
    return (
      <div style={base}>
        <div style={{ fontSize: 10, color: 'var(--ox-dim)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 1 }}>{payload.title}</div>
        <div style={{ fontSize: 12, color: 'var(--ox-text)', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{payload.content}</div>
      </div>
    );
  }

  if (action === 'export' && payload) {
    return (
      <div style={base}>
        <div style={{ fontSize: 10, color: 'var(--ox-dim)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 1 }}>Export ready</div>
        <pre style={{ fontSize: 10, color: 'var(--ox-muted)', overflowX: 'auto', maxHeight: 160, background: 'var(--ox-navy)', padding: '6px 8px', borderRadius: 4, border: '1px solid var(--ox-border)', marginBottom: 8 }}>
          {payload.data?.slice(0, 600)}{payload.data?.length > 600 ? '\n…' : ''}
        </pre>
        <button
          onClick={() => {
            const blob = new Blob([payload.data], { type: 'text/csv' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url; a.download = 'orchestratex-crm-export.csv'; a.click();
            URL.revokeObjectURL(url);
          }}
          style={{ background: 'var(--ox-blue)', border: 'none', borderRadius: 5, padding: '6px 14px', color: '#fff', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit' }}
        >
          ↓ Download CSV
        </button>
      </div>
    );
  }

  if (action === 'add_note' && payload) {
    return (
      <div style={base}>
        <div style={{ fontSize: 10, color: 'var(--ox-dim)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 1 }}>Note to append</div>
        <div style={{ fontSize: 11, color: 'var(--ox-text)', lineHeight: 1.6, marginBottom: 8 }}>{payload.note}</div>
        <button
          onClick={() => onAction('add_note', payload)}
          style={{ background: 'var(--ox-navy3)', border: '1px solid var(--ox-border2)', borderRadius: 5, padding: '6px 14px', color: 'var(--ox-blue2)', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit' }}
        >
          Add to record →
        </button>
      </div>
    );
  }

  return null;
}

// ── MAIN AGENT PANE ───────────────────────────────────────────────────────────
export default function AgentPane({ user, onAgentAction }) {
  const [messages, setMessages] = useState([
    {
      id: 0, role: 'assistant', time: now(),
      content: `Hi ${user.name.split(' ')[0]} — I'm the OrchestrateX CRM Agent. I have full access to your BD pipeline: ${PROSPECTS.length} prospects across federal, enterprise, channel partners, and investors.\n\nAsk me to filter records, draft outreach, summarize pipeline, update stages, or export data. Use the quick prompts below or type anything.`,
      action: null, payload: null,
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  function now() {
    return new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  async function send(text) {
    if (!text.trim() || loading) return;
    const userMsg = { id: Date.now(), role: 'user', time: now(), content: text, action: null, payload: null };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    const newHistory = [...history, { role: 'user', content: text }];

    try {
      const resp = await fetch('https://crm.orcx.ai/api/anthropic', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': process.env.REACT_APP_ANTHROPIC_KEY || '',
          'anthropic-version': '2023-06-01',
          'anthropic-dangerous-direct-browser-calls': 'true',
        },
        body: JSON.stringify({
          model: 'claude-sonnet-4-6',
          max_tokens: 1000,
          system: buildSystemPrompt(user),
          messages: newHistory,
        }),
      });

      const data = await resp.json();
      const raw = data.content?.find(b => b.type === 'text')?.text || '{"message":"Sorry, I could not process that request.","action":"none","payload":null}';

      let parsed;
      try {
        const cleaned = raw.replace(/```json|```/g, '').trim();
        parsed = JSON.parse(cleaned);
      } catch {
        parsed = { message: raw, action: 'none', payload: null };
      }

      const assistantMsg = {
        id: Date.now() + 1, role: 'assistant', time: now(),
        content: parsed.message || '',
        action: parsed.action || 'none',
        payload: parsed.payload || null,
      };

      setMessages(prev => [...prev, assistantMsg]);
      setHistory([...newHistory, { role: 'assistant', content: raw }]);
    } catch (err) {
      setMessages(prev => [...prev, {
        id: Date.now() + 2, role: 'assistant', time: now(),
        content: `Connection error: ${err.message}. Ensure the app is running with a valid Anthropic API key.`,
        action: null, payload: null,
      }]);
    }

    setLoading(false);
  }

  function handleAction(type, data) {
    if (onAgentAction) onAgentAction(type, data);

    const confirmMap = {
      filter: `Filtered table to ${Array.isArray(data) ? data.length : '?'} matching records.`,
      highlight: `Opened prospect record.`,
      update_stage: `Stage updated to "${data?.newStage}".`,
      add_note: `Intelligence note appended to record.`,
    };
    if (confirmMap[type]) {
      setMessages(prev => [...prev, { id: Date.now(), role: 'system', content: confirmMap[type] }]);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--ox-navy)' }}>

      {/* Header */}
      <div style={{ padding: '14px 20px 10px', borderBottom: '1px solid var(--ox-border)', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--ox-navy3)', border: '1px solid var(--ox-border2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>⬡</div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--ox-text)' }}>Quantum CRM Agent</div>
            <div style={{ fontSize: 10, color: 'var(--ox-teal)', display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--ox-teal)', display: 'inline-block' }} />
              LLM · {PROSPECTS.length} records in context
            </div>
          </div>
          <div style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--ox-dim)', textAlign: 'right' }}>
            <div>BD · Partnerships · Product</div>
            <div style={{ color: 'var(--ox-violet)' }}>MCP+ Agentic Layer</div>
          </div>
        </div>
      </div>

      {/* Quick prompts */}
      <div style={{ padding: '10px 16px 6px', borderBottom: '1px solid var(--ox-border)', flexShrink: 0, overflowX: 'auto' }}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {EXAMPLE_PROMPTS.map((ep, i) => (
            <button
              key={i}
              onClick={() => send(ep.prompt)}
              disabled={loading}
              style={{
                background: 'var(--ox-navy3)', border: '1px solid var(--ox-border)', borderRadius: 20,
                padding: '4px 10px', fontSize: 10, color: 'var(--ox-muted)', cursor: 'pointer',
                fontFamily: 'inherit', whiteSpace: 'nowrap', transition: 'all .15s',
              }}
              onMouseOver={e => { e.target.style.borderColor = 'var(--ox-blue2)'; e.target.style.color = 'var(--ox-blue2)'; }}
              onMouseOut={e => { e.target.style.borderColor = 'var(--ox-border)'; e.target.style.color = 'var(--ox-muted)'; }}
            >
              {ep.label}
            </button>
          ))}
        </div>
      </div>

      {/* Message thread */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 16px 8px' }}>
        {messages.map(msg => (
          <MessageBubble key={msg.id} msg={msg} onAction={handleAction} />
        ))}

        {loading && (
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 14 }}>
            <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--ox-navy3)', border: '1px solid var(--ox-border2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, flexShrink: 0 }}>⬡</div>
            <div style={{ background: 'var(--ox-navy3)', border: '1px solid var(--ox-border)', borderRadius: '2px 12px 12px 12px', padding: '12px 16px' }}>
              <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                {[0, 1, 2].map(i => (
                  <div key={i} style={{
                    width: 6, height: 6, borderRadius: '50%', background: 'var(--ox-blue2)',
                    animation: `pulse 1.2s ease-in-out ${i * 0.2}s infinite`,
                  }} />
                ))}
              </div>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input bar */}
      <div style={{ padding: '12px 16px', borderTop: '1px solid var(--ox-border)', flexShrink: 0 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <textarea
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(input); }
            }}
            placeholder="Ask about pipeline, draft outreach, filter records, update stages…"
            rows={2}
            style={{
              flex: 1, background: 'var(--ox-navy3)', border: '1px solid var(--ox-border)',
              borderRadius: 8, padding: '9px 12px', color: 'var(--ox-text)', fontSize: 12.5,
              fontFamily: 'inherit', resize: 'none', outline: 'none', lineHeight: 1.5,
            }}
            onFocus={e => { e.target.style.borderColor = 'var(--ox-blue)'; }}
            onBlur={e => { e.target.style.borderColor = 'var(--ox-border)'; }}
          />
          <button
            onClick={() => send(input)}
            disabled={loading || !input.trim()}
            style={{
              background: loading || !input.trim() ? 'var(--ox-navy3)' : 'var(--ox-blue)',
              border: `1px solid ${loading || !input.trim() ? 'var(--ox-border)' : 'var(--ox-blue)'}`,
              borderRadius: 8, padding: '9px 16px', color: loading || !input.trim() ? 'var(--ox-dim)' : '#fff',
              fontSize: 13, cursor: loading || !input.trim() ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit', transition: 'all .15s', flexShrink: 0, alignSelf: 'stretch',
            }}
          >
            {loading ? '…' : '↑'}
          </button>
        </div>
        <div style={{ fontSize: 10, color: 'var(--ox-dim)', marginTop: 6 }}>
          Enter to send · Shift+Enter for new line · Powered by Claude claude-sonnet-4-6
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 80%, 100% { opacity: .2; transform: scale(.8); }
          40% { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
}
